import React, { useState } from 'react'
import moment from 'moment'
import { DataCard, DateBasedChart, EmptyGrid, GlobalLoader, XYChart } from '@reactiveonline/reactive_admin_shared-packages'
import GlobalImports from '../shared/GlobalImports'
import PresetDateRangePicker from '../shared/PresetDateRangePicker'

export default function Dashboard({ appProps, chartColors, dashboardData, fetchDashboardInfoPath }) {
  const translations = Partners.translations.dashboard
  const [currentDashboardData, setCurrentDashboardData] = useState(dashboardData)
  const [loading, setLoading] = useState(false)
  const affiliateProgramEnabled = !!currentDashboardData.affiliateProgramEnabled
  const directorScope = currentDashboardData.scope === 'director'
  const [dateRange, setDateRange] = useState({
    start_date: moment(dashboardData.period?.startDate),
    end_date: moment(dashboardData.period?.endDate)
  })

  const money = cents => `${ ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) } EUR`
  const number = value => (value || 0).toLocaleString()

  const registrationsByMonth = (currentDashboardData.registrationsByMonth || []).map(item => ({
    date: item.month,
    value: item.registrations
  }))

  const commissionsByMonth = (currentDashboardData.commissionsByMonth || []).map(item => ({
    date: item.month,
    value: (item.commissionAmountCents || 0) / 100
  }))

  const paymentEventsByMonth = (currentDashboardData.paymentEventsByMonth || []).map(item => ({
    date: item.month,
    value: (item.netAmountCents || 0) / 100
  }))

  const registrationsByFlavour = (currentDashboardData.registrationsByFlavour || []).map(item => ({
    category: item.flavour,
    registrations: item.registrations
  }))

  const totalRegistrationsByFlavour = (currentDashboardData.totalRegistrationsByFlavour || []).map(item => ({
    category: item.flavour,
    registrations: item.registrations
  }))

  const commissionsByStatus = (currentDashboardData.commissionsByStatus || []).map(item => ({
    category: item.status,
    amount: (item.commissionAmountCents || 0) / 100
  }))

  const topOrganizations = (currentDashboardData.topOrganizations || []).map(item => ({
    category: item.organizationName,
    amount: (item.commissionAmountCents || 0) / 100
  }))
  const registrationFlavourCards = currentDashboardData.registrationFlavourCards || []

  function handleApplyDateRange(updatedDateRange) {
    setDateRange(updatedDateRange)
    setLoading(true)

    Rails.ajax({
      type: 'GET',
      url: `${ fetchDashboardInfoPath }?date_range=${ JSON.stringify(updatedDateRange) }`,
      dataType: 'json',
      success: res => {
        setCurrentDashboardData(res)
        setLoading(false)
      },
      error: () => setLoading(false)
    })
  }

  const ChartCard = ({ title, children, empty }) => (
    <div className='dashboard-item-wrapper'>
      <div className='card' style={{ height: '100%' }}>
        <div className='card-content' style={{ height: '100%' }}>
          <div className='card-title'>{ title }</div>
          <div className='card-body' style={{ height: '100%' }}>
            { empty ?
              <div className='flex-box items-center content-center' style={{ height: '100%' }}>
                <EmptyGrid
                  noResourcesTitle={ translations.no_data }
                  iconName='fa-chart-scatter-bubble'
                  disableReload
                />
              </div>
              :
              children
            }
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className='page-wrapper-header flex-box content-space-between'>
        <div className='page-header'>
          <i className='fa-light fa-chart-line' />
          <div className='flex-box flex-column'>
            <h1 className='page-header-title'>{ translations.title }</h1>
            <div className='m-t-5'>
              { !directorScope && currentDashboardData.organizationName ? `${ currentDashboardData.organizationName } | ` : '' }
              { currentDashboardData.period?.startDate } - { currentDashboardData.period?.endDate }
            </div>
          </div>
        </div>

        <div className='flex-box items-center'>
          <PresetDateRangePicker
            value={ dateRange }
            onApply={ handleApplyDateRange }
            maxDateRangeDays={ 365 }
          />
        </div>
      </div>

      { loading && <GlobalLoader fullPage /> }

      <div className='flex-box flex-wrap m-b-20' style={{ gap: 20 }}>
        { directorScope &&
          <div className='quick-data-card-wrapper flex-1' style={{ width: 'initial' }}>
            <DataCard icon='fa-building' title={ translations.organizations } data={ number(currentDashboardData.totals?.organizations) } />
          </div>
        }
        { !directorScope &&
          <div className='quick-data-card-wrapper flex-1' style={{ width: 'initial' }}>
            <DataCard icon='fa-user-tie' title={ translations.administrators } data={ number(currentDashboardData.totals?.administrators) } />
          </div>
        }
        { affiliateProgramEnabled &&
          <div className='quick-data-card-wrapper flex-1' style={{ width: 'initial' }}>
            <DataCard icon='fa-ticket' title={ translations.active_affiliate_codes } data={ number(currentDashboardData.totals?.activeAffiliateCodes) } />
          </div>
        }
        <div className='quick-data-card-wrapper flex-1' style={{ width: 'initial' }}>
          <DataCard icon='fa-address-card' title={ translations.registrations } data={ number(currentDashboardData.totals?.registrations) } />
        </div>
        { affiliateProgramEnabled &&
          <div className='quick-data-card-wrapper flex-1' style={{ width: 'initial' }}>
            <DataCard icon='fa-money-bill-trend-up' title={ translations.payable_commissions } data={ money(currentDashboardData.totals?.payableCommissionsAmountCents) } />
          </div>
        }
      </div>

      <div className='flex-box flex-wrap m-b-20' style={{ gap: 20 }}>
        { registrationFlavourCards.map(item => (
          <div
            key={ item.application_name }
            className='quick-data-card-wrapper flex-1'
            style={{ width: 'auto' }}
          >
            <DataCard
              icon={ item.icon }
              title={ item.application_name }
              data={ item.number_of_registrations }
              path={ item.project_url }
            />
          </div>
        )) }
      </div>

      <div className='flex-box flex-wrap items-stretch dashboard-wrapper'>
        <ChartCard title={ translations.registrations_over_time } empty={ registrationsByMonth.every(item => item.value === 0) }>
          <DateBasedChart data={ registrationsByMonth } chartKey='directorRegistrationsByMonth' />
        </ChartCard>

        { affiliateProgramEnabled &&
          <ChartCard title={ translations.commissions_over_time } empty={ commissionsByMonth.every(item => item.value === 0) }>
            <DateBasedChart data={ commissionsByMonth } chartKey='directorCommissionsByMonth' />
          </ChartCard>
        }

        { affiliateProgramEnabled &&
          <ChartCard title={ translations.net_paid_over_time } empty={ paymentEventsByMonth.every(item => item.value === 0) }>
            <DateBasedChart data={ paymentEventsByMonth } chartKey='directorNetPaidByMonth' />
          </ChartCard>
        }

        <ChartCard title={ translations.registrations_by_flavour } empty={ registrationsByFlavour.length === 0 }>
          <XYChart
            data={ registrationsByFlavour }
            seriesLabels={ [{ registrations: translations.registrations }] }
            xAxisTitle=''
            yAxisTitle=''
            columnWidth={ 15 }
            chartColors={ chartColors }
            chartKey='directorRegistrationsByFlavour'
          />
        </ChartCard>

        <ChartCard title={ translations.total_registrations_by_flavour } empty={ totalRegistrationsByFlavour.length === 0 }>
          <XYChart
            data={ totalRegistrationsByFlavour }
            seriesLabels={ [{ registrations: translations.registrations }] }
            xAxisTitle=''
            yAxisTitle=''
            columnWidth={ 15 }
            chartColors={ chartColors }
            chartKey='directorTotalRegistrationsByFlavour'
          />
        </ChartCard>

        { affiliateProgramEnabled &&
          <ChartCard title={ translations.commissions_by_status } empty={ commissionsByStatus.length === 0 }>
            <XYChart
              data={ commissionsByStatus }
              seriesLabels={ [{ amount: translations.amount }] }
              xAxisTitle=''
              yAxisTitle=''
              columnWidth={ 15 }
              chartColors={ chartColors }
              chartKey='directorCommissionsByStatus'
            />
          </ChartCard>
        }

        { affiliateProgramEnabled && directorScope &&
          <ChartCard title={ translations.top_organizations } empty={ topOrganizations.length === 0 }>
            <XYChart
              data={ topOrganizations }
              seriesLabels={ [{ amount: translations.amount }] }
              xAxisTitle=''
              yAxisTitle=''
              columnWidth={ 15 }
              chartColors={ chartColors }
              chartKey='directorTopOrganizations'
            />
          </ChartCard>
        }
      </div>

      <GlobalImports appProps={ appProps } />
    </>
  )
}
